/**
 * Maps a DOM selection inside a rendered body back to a Markdown source range.
 *
 * This is the piece that makes span creation possible over rendered Markdown.
 * ui-common's `calculateTextSelectionOffsets` cannot be reused: it derives an
 * offset by concatenating the container's DOM text nodes and assuming the
 * result equals the document text, which holds for the flat plaintext viewer
 * and fails here. The rendered DOM contains "History and Physical"; the source
 * contains "## History and Physical". Every marker the renderer consumed is an
 * offset the DOM no longer accounts for.
 *
 * Instead each text fragment is wrapped, at parse time, in an element carrying
 * its exact source range (see ANCHOR_NODE and MARK_NODE). Resolving a selection
 * is then a matter of reading the range off the nearest wrapping ancestor and
 * adding the offset within it -- no re-derivation, no drift.
 */

/** Attribute names written by AnnotationAnchor and AnnotationMark. */
const SRC_START_ATTR = 'data-src-start';
const SRC_END_ATTR = 'data-src-end';

export type SourceRange = {
  start: number;
  end: number;
};

type Endpoint = {
  /** The wrapping element's source range. */
  elementStart: number;
  elementEnd: number;
  /** Offset of the caret within that element's rendered text. */
  textOffset: number;
  /** Rendered text length of that element. */
  textLength: number;
};

/** Nearest ancestor (inclusive) carrying a source range. */
function closestAnchor(node: Node | null): HTMLElement | null {
  let current: Node | null = node;

  while (current) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      const element = current as HTMLElement;
      if (element.hasAttribute(SRC_START_ATTR)) {
        return element;
      }
    }
    current = current.parentNode;
  }

  return null;
}

/** Rendered-text offset of `node`+`offset` within `element`. */
function textOffsetWithin(
  element: HTMLElement,
  node: Node,
  offset: number
): number {
  // A caret addressed against the element itself (rather than a text node)
  // counts whole children, which is what `offset` means in that case.
  if (node === element) {
    let counted = 0;
    for (
      let index = 0;
      index < offset && index < element.childNodes.length;
      index++
    ) {
      counted += element.childNodes[index].textContent?.length ?? 0;
    }
    return counted;
  }

  let total = 0;
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
  let textNode = walker.nextNode();

  while (textNode) {
    if (textNode === node) {
      return total + offset;
    }
    total += textNode.textContent?.length ?? 0;
    textNode = walker.nextNode();
  }

  // Caret is not inside this element's text; clamp rather than guess.
  return total;
}

function endpointFor(node: Node, offset: number): Endpoint | null {
  const element = closestAnchor(node);
  if (!element) {
    return null;
  }

  const elementStart = Number(element.getAttribute(SRC_START_ATTR));
  const elementEnd = Number(element.getAttribute(SRC_END_ATTR));
  if (!Number.isFinite(elementStart) || !Number.isFinite(elementEnd)) {
    return null;
  }

  return {
    elementStart,
    elementEnd,
    textOffset: textOffsetWithin(element, node, offset),
    textLength: element.textContent?.length ?? 0
  };
}

/**
 * Source offset for one endpoint.
 *
 * The fragment's rendered text and its source slice can differ in length --
 * remark decodes character references and escapes, so `H&amp;P` is 7 source
 * characters and 3 rendered ones. Interpolating proportionally would put a
 * boundary mid-entity. Clamping to the fragment's own bounds instead keeps the
 * range honest: it may be a few characters wide of the caret inside such a
 * fragment, which is visible and safe, rather than silently misaligned.
 */
function sourceOffset(endpoint: Endpoint, isEnd: boolean): number {
  const { elementStart, elementEnd, textOffset, textLength } = endpoint;
  const sourceWidth = elementEnd - elementStart;

  // The common case by far: rendered text and source slice are the same
  // length, so the caret maps one-to-one.
  if (sourceWidth === textLength) {
    return elementStart + textOffset;
  }

  if (textOffset <= 0) {
    return elementStart;
  }
  if (textOffset >= textLength) {
    return elementEnd;
  }

  // Divergent fragment with an interior caret: snap outward, so the range
  // covers at least what the user selected.
  return isEnd ? elementEnd : elementStart;
}

/**
 * Resolves a selection to a source range, or null when it cannot be resolved.
 *
 * Returns null for a collapsed selection, for a selection outside the
 * container, and for one whose endpoints carry no source range -- all of which
 * are ordinary (a click, a stray drag), not errors.
 */
export function selectionToSourceRange(
  container: HTMLElement | null,
  selection: Selection | null
): SourceRange | null {
  if (!container || !selection || selection.isCollapsed) {
    return null;
  }
  if (selection.rangeCount === 0 || selection.toString().trim().length === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);

  // Ignore selections that stray outside this document body. A coder dragging
  // past the end of the card would otherwise create a span from a caret in a
  // neighbouring component.
  if (
    !container.contains(range.startContainer) ||
    !container.contains(range.endContainer)
  ) {
    return null;
  }

  const startEndpoint = endpointFor(range.startContainer, range.startOffset);
  const endEndpoint = endpointFor(range.endContainer, range.endOffset);
  if (!startEndpoint || !endEndpoint) {
    return null;
  }

  const start = sourceOffset(startEndpoint, false);
  const end = sourceOffset(endEndpoint, true);

  // A backwards or empty range means the endpoints resolved to the same
  // fragment boundary; there is no span to create.
  if (end <= start) {
    return null;
  }

  return { start, end };
}
